"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import InputAdornment from "@mui/material/InputAdornment";
import CloseIcon from "@mui/icons-material/Close";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Autocomplete,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Divider,
  Alert,
} from "@mui/material";

import { useTheme, styled } from "@mui/material/styles";

import {
  Add,
  Delete,
  Edit,
  Visibility,
  Close,
  CheckCircle,
  Cancel,
  FilterList,
  Search,
} from "@mui/icons-material";

import { useBranch } from "./../../layouts/BranchContext";

/* =========================================================
   BASE URL
========================================================= */

const API_BASE_URL = "https://wims.revisetoexcel.com/api";

/* =========================================================
   AXIOS
========================================================= */

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

/* =========================================================
   TOKEN
========================================================= */

const getAccessToken = () => {
  if (typeof window === "undefined") return null;

  const tokenKeys = [
    "accessToken",
    "access_token",
    "token",
  ];

  for (const key of tokenKeys) {
    const localToken = localStorage.getItem(key);

    if (localToken) return localToken;

    const sessionToken = sessionStorage.getItem(key);

    if (sessionToken) return sessionToken;
  }

  return null;
};

/* =========================================================
   INTERCEPTOR
========================================================= */

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* =========================================================
   ENDPOINTS
   (per Swagger — all library/book-reservation APIs)
========================================================= */

const ENDPOINTS = {
  reservationList: "/library/book-reservation/list/",

  reservationCreate: "/library/book-reservation/create/",

  reservationDetail: (id) => `/library/book-reservation/${id}/`,

  reservationUpdate: (id) => `/library/book-reservation/update/${id}/`,

  reservationDelete: (id) => `/library/book-reservation/delete/${id}/`,

  reservationStatus: (id) => `/library/book-reservation/status/${id}/`,

  memberDropdown: "/library/library-member/dropdown/",

  bookList: "/library/book-master/list/",
};

/* =========================================================
   SHARED SCROLLBAR
========================================================= */

const scrollbarStyles = (theme) => {
  const dark = theme.palette.mode === "dark";

  return {
    "&::-webkit-scrollbar": {
      width: "6px",
      height: "6px",
      background: "transparent",
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor: dark
        ? "rgba(255,255,255,0.25)"
        : "rgba(0,0,0,0.20)",
      borderRadius: "5px",
    },

    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: dark
        ? "rgba(255,255,255,0.40)"
        : "rgba(0,0,0,0.30)",
    },

    scrollbarWidth: "thin",
  };
};

const CustomScrollbar = styled("div")(
  ({ theme }) => ({
    overflowY: "auto",
    backgroundColor:
      theme.palette.background.paper,
    ...scrollbarStyles(theme),
  })
);

/* =========================================================
   USER
========================================================= */

const getStoredUser = () => {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    return JSON.parse(
      sessionStorage.getItem("user") || "{}"
    );
  } catch {
    return {};
  }
};

/* =========================================================
   DATE
========================================================= */

const getToday = () =>
  new Date().toISOString().split("T")[0];

const getDatePlusDays = (dateString, days) => {
  if (!dateString) return "";

  const date = new Date(`${dateString}T00:00:00`);
  date.setDate(date.getDate() + days);

  return date.toISOString().split("T")[0];
};

/* =========================================================
   EMPTY FORM

   NOTE (Swagger):
   - organization / branch come from session user +
     global branch selector — no dropdowns on this page.
   - priority is AUTO-CALCULATED by the backend
     (queue priority) — never sent from the UI.
========================================================= */

const getEmptyForm = () => {
  const reservationDate = getToday();

  return {
    library_member: "",
    book: "",
    reservation_date: reservationDate,
    expiry_date: getDatePlusDays(reservationDate, 7),
    reservation_status: "pending",
    notification_sent: false,
    remarks: "",
    is_active: true,
  };
};

/* =========================================================
   NORMALIZE ARRAY
========================================================= */

const normalizeArray = (data) => {
  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.results)) {
    return data.results;
  }

  if (Array.isArray(data?.data)) return data.data;

  if (Array.isArray(data?.records)) {
    return data.records;
  }

  if (Array.isArray(data?.items)) return data.items;

  return [];
};

/* =========================================================
   ID HELPER
========================================================= */

const getIdValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "object") {
    return String(value.id ?? value.value ?? "");
  }

  return String(value);
};

/* =========================================================
   RESERVATION STATUS OPTIONS
   (Swagger enum — 5 values)
========================================================= */

const RESERVATION_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "ready", label: "Ready for Pickup" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "expired", label: "Expired" },
];
/* =========================================================
   COMPONENT
========================================================= */

export default function BookReservation() {
  const theme = useTheme();
  const navigate = useNavigate();

  const isDark = theme.palette.mode === "dark";

  // Global branch selector (navbar)
  const { selectedBranchId } = useBranch();

  const user = useMemo(() => getStoredUser(), []);

  const organizationId =
    user?.organization_id || "";

  /* =======================================================
     COLORS
  ======================================================= */

  const textColor = isDark ? "#f1f5f9" : "#1f2937";

  const secondaryTextColor = isDark
    ? "#cbd5e1"
    : "#6b7280";

  const modalTextColor = isDark ? "#f1f5f9" : "#374151";

  const borderColor = isDark
    ? "rgba(255,255,255,0.16)"
    : "rgba(0,0,0,0.15)";

  const tableRowHover = isDark
    ? "rgba(255,255,255,0.05)"
    : "rgba(0,0,0,0.04)";

  /* =======================================================
     SHARED DATE PICKER PROPS
     (dark mode safe)
  ======================================================= */

  const datePickerSx = {
    ...scrollbarStyles(theme),

    "& .MuiPickersLayout-root": {
      backgroundColor:
        theme.palette.background.paper,
      colorScheme: isDark ? "dark" : "light",
    },
  };

  /* =======================================================
     SELECT / TEXTFIELD STYLES
  ======================================================= */

  const inputLabelSx = {
    color: isDark
      ? "rgba(255,255,255,0.75)"
      : "#4b5563",

    "&.Mui-focused": {
      color: theme.palette.primary.main,
    },
  };

  const selectSx = {
    color: textColor,

    "& .MuiSelect-select": {
      color: `${textColor} !important`,
      minHeight: "40px",
      display: "flex",
      alignItems: "center",
    },

    "& .MuiSvgIcon-root": {
      color: `${textColor} !important`,
    },

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor,
    },

    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },

    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
      {
        borderColor: theme.palette.primary.main,
      },

    "&.Mui-disabled": {
      color: `${secondaryTextColor} !important`,
    },

    "&.Mui-disabled .MuiSelect-select": {
      color: `${secondaryTextColor} !important`,
      WebkitTextFillColor: `${secondaryTextColor} !important`,
    },

    "&.Mui-disabled .MuiOutlinedInput-notchedOutline":
      {
        borderColor,
      },
  };

  const textFieldSx = {
    "& .MuiInputLabel-root": {
      color: isDark
        ? "rgba(255,255,255,0.75)"
        : "#4b5563",
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },

    "& .MuiOutlinedInput-root": {
      color: textColor,

      "& fieldset": {
        borderColor,
      },

      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },

      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },

    "& input": {
      color: textColor,
    },

    "& textarea": {
      color: textColor,
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        color: textColor,

        ...scrollbarStyles(theme),

        "& .MuiMenuItem-root": {
          color: textColor,
        },

        "& .MuiMenuItem-root:hover": {
          backgroundColor: tableRowHover,
        },

        "& .MuiMenuItem-root.Mui-selected": {
          backgroundColor: theme.palette.action.selected,
          color: textColor,
        },
      },
    },
  };

  /* =======================================================
     LIST

     Swagger: /list/ returns a plain array
     (no page / page_size params) — pagination
     is done client-side.
  ======================================================= */

  const [allRows, setAllRows] = useState([]);

  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  const rows = useMemo(() => {
    const start = page * rowsPerPage;

    return allRows.slice(
      start,
      start + rowsPerPage
    );
  }, [allRows, page, rowsPerPage]);

  /* =======================================================
     SEARCH
  ======================================================= */

  const [searchInput, setSearchInput] = useState("");

  const [search, setSearch] = useState("");

  /* =======================================================
     FILTERS

     Swagger list filters:
     member_type, library_member, book,
     reservation_status, reservation_date,
     expiry_date, is_active
  ======================================================= */

  const EMPTY_FILTERS = {
    member_type: "",
    library_member: "",
    book: "",
    reservation_status: "",
    reservation_date: "",
    expiry_date: "",
    is_active: "",
  };

  const [filters, setFilters] = useState({
    ...EMPTY_FILTERS,
  });

  /* =======================================================
     DROPDOWNS
  ======================================================= */

  const [members, setMembers] = useState([]);

  const [books, setBooks] = useState([]);

  /* =======================================================
     MODALS
  ======================================================= */

  const [openDialog, setOpenDialog] =
    useState(false);

  const [dialogMode, setDialogMode] =
    useState("create");

  const [selectedId, setSelectedId] =
    useState(null);

  const [form, setForm] =
    useState(getEmptyForm());

  const [formLoading, setFormLoading] =
    useState(false);

  const [openFilterDialog, setOpenFilterDialog] =
    useState(false);

  /* =======================================================
     CONFIRMATION MODAL
  ======================================================= */

  const [confirmDialog, setConfirmDialog] =
    useState({
      open: false,
      type: "",
      row: null,
    });

  const [confirmLoading, setConfirmLoading] =
    useState(false);

  /* =======================================================
     MESSAGES
  ======================================================= */

  const [error, setError] = useState("");

  const [success, setSuccess] = useState("");

  /* =======================================================
     ERROR HELPER
  ======================================================= */

  const getErrorMessage = (err, fallback) => {
    console.error("API ERROR:", err);

    if (err?.response?.status === 401) {
      return "Authentication failed. Please login again.";
    }

    if (err?.response?.status === 403) {
      return "You do not have permission to access this API.";
    }

    if (err?.response?.status === 404) {
      return "API endpoint not found (404). Please check the backend API URL.";
    }

    const data = err?.response?.data;

    if (!data) {
      return err?.message || fallback;
    }

    if (typeof data === "string") {
      return data;
    }

    if (data.detail) {
      return String(data.detail);
    }

    if (data.message) {
      return typeof data.message === "string"
        ? data.message
        : JSON.stringify(data.message);
    }

    if (data.error) {
      return typeof data.error === "string"
        ? data.error
        : JSON.stringify(data.error);
    }

    if (data.errors && typeof data.errors === "object") {
      const messages = [];

      Object.entries(data.errors).forEach(
        ([field, value]) => {
          const message = Array.isArray(value)
            ? value.join(" ")
            : String(value);

          messages.push(`${field}: ${message}`);
        }
      );

      return messages.join(" ");
    }

    return fallback;
  };

  /* =======================================================
     ACTIVE FILTER COUNT
  ======================================================= */

  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter(
      (value) =>
        value !== "" &&
        value !== null &&
        value !== undefined
    ).length;
  }, [filters]);

  /* =======================================================
     FETCH RESERVATIONS

     GET /library/book-reservation/list/
     Filters (Swagger):
     search, branch, member_type, library_member,
     book, reservation_status, reservation_date,
     expiry_date, is_active
  ======================================================= */

  const fetchReservations = useCallback(
    async ({
      branch = selectedBranchId,
      searchValue = search,
      filterValues = filters,
    } = {}) => {
      if (!organizationId || !branch) {
        setAllRows([]);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const params = {
          organization: organizationId,
          branch: branch,
        };

        if (searchValue) {
          params.search = searchValue;
        }

        Object.entries(filterValues).forEach(
          ([key, value]) => {
            if (
              value !== "" &&
              value !== null &&
              value !== undefined
            ) {
              params[key] = value;
            }
          }
        );

        const response = await api.get(
          ENDPOINTS.reservationList,
          { params }
        );

        const list = normalizeArray(response.data);

        setAllRows(list);
      } catch (err) {
        setError(
          getErrorMessage(
            err,
            "Failed to load book reservations."
          )
        );

        setAllRows([]);
      } finally {
        setLoading(false);
      }
    },
    [
      organizationId,
      selectedBranchId,
      search,
      filters,
    ]
  );

  /* =======================================================
     FETCH MEMBERS

     GET /library/library-member/dropdown/
  ======================================================= */

  const fetchMembers = useCallback(
    async (memberType = "") => {
      if (!organizationId || !selectedBranchId) {
        setMembers([]);
        return;
      }

      try {
        const params = {
          organization: organizationId,
          branch: selectedBranchId,
        };

        if (memberType) {
          params.member_type = memberType;
        }

        const response = await api.get(
          ENDPOINTS.memberDropdown,
          { params }
        );

        setMembers(normalizeArray(response.data));
      } catch (err) {
        console.error("Member API Error:", err);
        setMembers([]);
      }
    },
    [organizationId, selectedBranchId]
  );

  /* =======================================================
     FETCH BOOKS

     GET /library/book-master/list/
     Used to read available copies — reservation
     is only allowed when a book has 0 available
     copies (enforced by the Create API too).
  ======================================================= */

  const fetchBooks = useCallback(async () => {
    if (!organizationId || !selectedBranchId) {
      setBooks([]);
      return;
    }

    try {
      const params = {
        organization: organizationId,
        branch: selectedBranchId,
        page_size: 500,
      };

      const response = await api.get(
        ENDPOINTS.bookList,
        { params }
      );

      setBooks(normalizeArray(response.data));
    } catch (err) {
      console.error("Book API Error:", err);
      setBooks([]);
    }
  }, [organizationId, selectedBranchId]);

  /* =======================================================
     INITIAL LOAD / BRANCH CHANGE
  ======================================================= */

  useEffect(() => {
    setPage(0);

    fetchReservations({
      branch: selectedBranchId,
    });

    fetchMembers();

    fetchBooks();
  }, [selectedBranchId]);

  /* =======================================================
     SEARCH
  ======================================================= */

  const handleSearch = () => {
    const value = searchInput.trim();

    setSearch(value);
    setPage(0);

    fetchReservations({
      searchValue: value,
      currentPage: 0,
    });
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(0);

    fetchReservations({
      searchValue: "",
      currentPage: 0,
    });
  };

  /* =======================================================
     FILTER HANDLERS
  ======================================================= */

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApplyFilters = () => {
    setPage(0);

    fetchReservations({
      filterValues: filters,
      currentPage: 0,
    });

    setOpenFilterDialog(false);
  };

  const handleResetFilters = () => {
    setFilters({ ...EMPTY_FILTERS });
    setPage(0);

    fetchReservations({
      filterValues: EMPTY_FILTERS,
      currentPage: 0,
    });

    setOpenFilterDialog(false);
  };

  /* =======================================================
     FORM CHANGE
  ======================================================= */

  const handleFormChange = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* =======================================================
     CREATE
  ======================================================= */

  const handleCreate = () => {
    setDialogMode("create");
    setSelectedId(null);
    setForm(getEmptyForm());

    setError("");
    setSuccess("");

    fetchMembers();
    fetchBooks();

    setOpenDialog(true);
  };

  /* =======================================================
     BOOK AVAILABILITY
  ======================================================= */

  const getBookAvailability = (book) => {
    if (!book) return null;

    const value =
      book.available_copies ??
      book.availableCopies ??
      book.available_quantity ??
      book.availableQuantity ??
      book.available_books ??
      book.availableBooks ??
      book.copies_available ??
      book.copiesAvailable ??
      book.availability ??
      book.available;

    if (typeof value === "boolean") {
      return value ? 1 : 0;
    }

    if (
      value !== undefined &&
      value !== null &&
      value !== ""
    ) {
      const number = Number(value);

      return Number.isNaN(number) ? null : number;
    }

    return null;
  };

  const isBookReservable = (book) => {
    const availability = getBookAvailability(book);

    // Unknown availability — let the backend validate.
    if (availability === null) return true;

    // Swagger: reservation allowed only when
    // the book has NO available copies.
    return availability === 0;
  };

  /* =======================================================
     DETAIL

     GET /library/book-reservation/{id}/
  ======================================================= */

  const fetchDetail = async (id, mode = "view") => {
    try {
      setFormLoading(true);
      setError("");

      const response = await api.get(
        ENDPOINTS.reservationDetail(id)
      );

      const d =
        response.data?.data ?? response.data;

      setForm({
        library_member: getIdValue(d.library_member),
        book: getIdValue(d.book),

        reservation_date: d.reservation_date
          ? d.reservation_date.slice(0, 10)
          : "",

        expiry_date: d.expiry_date
          ? d.expiry_date.slice(0, 10)
          : "",

        reservation_status:
          d.reservation_status || "pending",

        notification_sent:
          d.notification_sent ?? false,

        remarks: d.remarks || "",

        is_active: d.is_active ?? true,
      });

      setSelectedId(id);
      setDialogMode(mode);
      setOpenDialog(true);

      // Ensure dropdowns are populated for the
      // selected member / book labels.
      fetchMembers();
      fetchBooks();
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          "Failed to load reservation details."
        )
      );
    } finally {
      setFormLoading(false);
    }
  };

  /* =======================================================
     CREATE SUBMIT

     POST /library/book-reservation/create/
     Payload (Swagger — priority is NOT sent,
     backend calculates queue priority):
     {
       organization, branch, library_member, book,
       reservation_date, expiry_date, remarks
     }
  ======================================================= */

  const handleSubmit = async () => {
    if (!organizationId || !selectedBranchId) {
      setError(
        "Please select an organization and branch."
      );

      return;
    }

    if (
      !form.library_member ||
      !form.book ||
      !form.reservation_date
    ) {
      setError("Please fill all required fields.");

      return;
    }

    if (
      form.expiry_date &&
      form.expiry_date < form.reservation_date
    ) {
      setError(
        "Expiry date cannot be before the reservation date."
      );

      return;
    }

    const selectedBook = books.find(
      (book) =>
        String(book.id) === String(form.book)
    );

    if (selectedBook) {
      const availability =
        getBookAvailability(selectedBook);

      if (
        availability !== null &&
        availability > 0
      ) {
        setError(
          "This book is available. Please issue the book instead of reserving it."
        );

        return;
      }
    }

    try {
      setFormLoading(true);
      setError("");
      setSuccess("");

      const payload = {
        organization: Number(organizationId),
        branch: Number(selectedBranchId),
        library_member: Number(form.library_member),
        book: Number(form.book),
        reservation_date: form.reservation_date,
        expiry_date: form.expiry_date || null,
        remarks: form.remarks || null,
      };

      await api.post(
        ENDPOINTS.reservationCreate,
        payload
      );

      setSuccess(
        "Book reservation created successfully."
      );

      setOpenDialog(false);

      await fetchReservations();
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          "Failed to create reservation."
        )
      );
    } finally {
      setFormLoading(false);
    }
  };

  /* =======================================================
     UPDATE

     PUT /library/book-reservation/update/{id}/
     Payload (Swagger):
     {
       organization, branch, library_member, book,
       reservation_date, expiry_date,
       reservation_status, notification_sent,
       remarks, is_active
     }

     Reservation Code + queue priority are
     never modified.
  ======================================================= */

  const handleUpdate = async () => {
    if (!selectedId) {
      setError("Reservation ID is missing.");

      return;
    }

    if (
      !form.library_member ||
      !form.book ||
      !form.reservation_date
    ) {
      setError("Please fill all required fields.");

      return;
    }

    if (
      form.expiry_date &&
      form.expiry_date < form.reservation_date
    ) {
      setError(
        "Expiry date cannot be before the reservation date."
      );

      return;
    }

    try {
      setFormLoading(true);
      setError("");
      setSuccess("");

      const payload = {
        organization: Number(organizationId),
        branch: Number(selectedBranchId),
        library_member: Number(form.library_member),
        book: Number(form.book),
        reservation_date: form.reservation_date,
        expiry_date: form.expiry_date || null,
        reservation_status:
          form.reservation_status || "pending",
        notification_sent: Boolean(
          form.notification_sent
        ),
        remarks: form.remarks || null,
        is_active: Boolean(form.is_active),
      };

      await api.put(
        ENDPOINTS.reservationUpdate(selectedId),
        payload
      );

      setSuccess("Reservation updated successfully.");

      setOpenDialog(false);

      await fetchReservations();
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          "Failed to update reservation."
        )
      );
    } finally {
      setFormLoading(false);
    }
  };

  /* =======================================================
     CONFIRMATION HELPERS
  ======================================================= */

  const openDeleteConfirmation = (row) => {
    setConfirmDialog({
      open: true,
      type: "delete",
      row,
    });
  };

  const openStatusConfirmation = (row) => {
    setConfirmDialog({
      open: true,
      type: "status",
      row,
    });
  };

  const closeConfirmDialog = () => {
    if (confirmLoading) return;

    setConfirmDialog({
      open: false,
      type: "",
      row: null,
    });
  };

  /* =======================================================
     DELETE

     DELETE /library/book-reservation/delete/{id}/
     (soft delete)
  ======================================================= */

  const handleDelete = async () => {
    const row = confirmDialog.row;

    if (!row?.id) return;

    try {
      setConfirmLoading(true);
      setError("");
      setSuccess("");

      await api.delete(
        ENDPOINTS.reservationDelete(row.id)
      );

      setSuccess(
        "Reservation deleted successfully."
      );

      closeConfirmDialog();

      await fetchReservations();
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          "Failed to delete reservation."
        )
      );

      closeConfirmDialog();
    } finally {
      setConfirmLoading(false);
    }
  };

  /* =======================================================
     STATUS (ACTIVATE / DEACTIVATE)

     PATCH /library/book-reservation/status/{id}/
     Payload: { is_active }
  ======================================================= */

  const handleStatus = async () => {
    const row = confirmDialog.row;

    if (!row?.id) return;

    const newStatus = !row.is_active;

    try {
      setConfirmLoading(true);
      setError("");
      setSuccess("");

      await api.patch(
        ENDPOINTS.reservationStatus(row.id),
        { is_active: newStatus }
      );

      setSuccess(
        `Reservation ${
          newStatus ? "activated" : "deactivated"
        } successfully.`
      );

      closeConfirmDialog();

      await fetchReservations();
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          "Failed to update reservation status."
        )
      );

      closeConfirmDialog();
    } finally {
      setConfirmLoading(false);
    }
  };

  /* =======================================================
     LABEL HELPERS
  ======================================================= */

  const getMemberLabel = (member) => {
    if (typeof member === "string") return member;

    return (
      member?.label ||
      member?.member_name ||
      member?.name ||
      member?.full_name ||
      member?.member_code ||
      member?.member_id ||
      member?.id ||
      ""
    );
  };

  const getBookLabel = (book) => {
    if (typeof book === "string") return book;

    return (
      book?.display_name ||
      book?.label ||
      book?.book_title ||
      book?.title ||
      book?.book_name ||
      book?.book_code ||
      book?.id ||
      ""
    );
  };

  /* =======================================================
     STATUS CHIP
  ======================================================= */

  const statusChip = (row) => {
    const raw = row?.reservation_status;

    const value = String(raw || "").toLowerCase();

    return (
      <Chip
        size="small"
        label={
          row?.reservation_status_display ||
          (raw ? String(raw).toUpperCase() : "-")
        }
        sx={{
          fontWeight: 600,
          borderRadius: "20px",

          backgroundColor:
            value === "pending"
              ? "rgba(237,108,2,0.12)"
              : value === "confirmed"
              ? "rgba(25,118,210,0.12)"
              : value === "fulfilled"
              ? "rgba(46,125,50,0.12)"
              : value === "cancelled"
              ? "rgba(211,47,47,0.12)"
              : value === "expired"
              ? "rgba(108,117,125,0.12)"
              : "rgba(25,118,210,0.12)",

          color:
            value === "pending"
              ? theme.palette.warning.main
              : value === "confirmed"
              ? theme.palette.primary.main
              : value === "fulfilled"
              ? theme.palette.success.main
              : value === "cancelled"
              ? theme.palette.error.main
              : value === "expired"
              ? theme.palette.text.secondary
              : theme.palette.primary.main,

          border: "none",
        }}
      />
    );
  };

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <Box
      p={{ xs: 1, md: 3 }}
      sx={{ color: textColor }}
    >
      {/* HEADER */}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        gap={2}
        flexWrap="wrap"
      >
      
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          width={{ xs: "100%", sm: "auto" }}
        >
<TextField
  variant="outlined"
  placeholder="Search Reservation..."
  value={searchInput}
  onChange={(e) => setSearchInput(e.target.value)}
  onKeyDown={(e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  }}
  sx={{
    ...textFieldSx,

    width: 350,
    maxWidth: "100%",
    minWidth: { xs: "100%", sm: 260 },

    "& .MuiOutlinedInput-root": {
      height: 50,
      borderRadius: "30px",

      "& fieldset": {
        borderColor: borderColor,
      },

      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },

      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },

    "& .MuiInputBase-input": {
      color: textColor,
    },

    "& .MuiInputBase-input::placeholder": {
      color: secondaryTextColor,
      opacity: 1,
    },
  }}
  InputProps={{
    startAdornment: (
      <InputAdornment position="start">
        <Search
          sx={{
            color: theme.palette.primary.main,
            ml: 1,
          }}
        />
      </InputAdornment>
    ),

    endAdornment: searchInput ? (
      <InputAdornment position="end">
        <IconButton
          size="small"
          onClick={() => {
            setSearchInput("");
          }}
        >
          <CloseIcon
            fontSize="small"
            sx={{ color: secondaryTextColor }}
          />
        </IconButton>
      </InputAdornment>
    ) : null,
  }}
/>

          {search && (
            <Button
              variant="contained"
              onClick={handleClearSearch}
              sx={{
                height: 44,
                borderRadius: 2,
                fontWeight: 700,
              }}
            >
              Clear
            </Button>
          )}

          <Box
            sx={{
              position: "relative",
              display: "inline-flex",
            }}
          >
            <Button
              variant="contained"
              startIcon={<FilterList />}
              onClick={() =>
                setOpenFilterDialog(true)
              }
              sx={{
                minWidth: 125,
                height: 44,
                borderRadius: 2,
                fontWeight: 700,
              }}
            >
              Filters
            </Button>

            {activeFilterCount > 0 && (
              <Chip
                label={activeFilterCount}
                size="small"
                color="primary"
                sx={{
                  position: "absolute",
                  top: -10,
                  right: -8,
                  height: 22,
                  minWidth: 22,
                  fontWeight: 700,
                  pointerEvents: "none",
                }}
              />
            )}
          </Box>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
            sx={{
              minWidth: 175,
              height: 44,
              borderRadius: 2,
              fontWeight: 700,
            }}
          >
            ADD RESERVATION
          </Button>

          <Button
            variant="contained"
            startIcon={<CheckCircle />}
            onClick={() =>
              navigate(
                "/library-management/book-issue-transaction"
              )
            }
            sx={{
              minWidth: 125,
              height: 44,
              borderRadius: 2,
              fontWeight: 700,
            }}
          >
            ISSUE
          </Button>
        </Stack>
      </Box>

      {/* ALERTS */}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{
            mb: 2,

            ...(isDark && {
              backgroundColor: "#ffffff",
              color: "#111827",

              "& .MuiAlert-icon": {
                color: theme.palette.success.main,
              },

              "& .MuiAlert-message": {
                color: "#111827",
                fontWeight: 600,
              },
            }),
          }}
          onClose={() => setSuccess("")}
        >
          {success}
        </Alert>
      )}

      {/* =====================================================
          FILTER MODAL
      ===================================================== */}

      <Modal
        open={openFilterDialog}
        onClose={() => setOpenFilterDialog(false)}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "94%", sm: 680, md: 850 },
            maxWidth: "95%",
            maxHeight: "90vh",
            bgcolor: theme.palette.background.paper,
            color: modalTextColor,
            borderRadius: 3,
            boxShadow: 24,
            overflow: "hidden",
            outline: "none",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* HEADER */}

          <Box
            sx={{
              backgroundColor:
                theme.palette.primary.main,
              px: 2,
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: "#ffffff",
                  fontWeight: 700,
                }}
              >
                Reservation Filters
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255,255,255,0.85)",
                }}
              >
                Apply filters to book reservations
              </Typography>
            </Box>

            <IconButton
              onClick={() =>
                setOpenFilterDialog(false)
              }
              sx={{ color: "#ffffff" }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* BODY */}

          <CustomScrollbar
            sx={{
              p: 3,
              maxHeight: "calc(90vh - 135px)",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
              }}
            >
              {/* MEMBER TYPE */}

              <FormControl
                fullWidth
                size="medium"
                sx={{
                  "& .MuiInputLabel-root": inputLabelSx,
                }}
              >
                <InputLabel>
                  Member Type
                </InputLabel>

                <Select
                  label="Member Type"
                  value={filters.member_type || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "member_type",
                      e.target.value
                    )
                  }
                  sx={selectSx}
                  MenuProps={menuProps}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="student">
                    Student
                  </MenuItem>
                  <MenuItem value="staff">
                    Staff
                  </MenuItem>
                </Select>
              </FormControl>

              {/* LIBRARY MEMBER */}

              <Autocomplete
                fullWidth
                options={[{ id: "", label: "All" }, ...members]}
                value={
                  [{ id: "", label: "All" }, ...members].find(
                    (member) =>
                      String(member.id) ===
                      String(filters.library_member || "")
                  ) || null
                }
                onChange={(_, member) =>
                  handleFilterChange(
                    "library_member",
                    member?.id ? String(member.id) : ""
                  )
                }
                getOptionLabel={(member) =>
                  String(getMemberLabel(member) || "")
                }
                isOptionEqualToValue={(option, value) =>
                  String(option.id) === String(value.id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Library Member"
                    placeholder="Search members..."
                    sx={{
                      "& .MuiInputLabel-root": inputLabelSx,
                    }}
                  />
                )}
                sx={selectSx}
              />

              {/* BOOK */}

              <Autocomplete
                fullWidth
                options={[{ id: "", label: "All" }, ...books]}
                value={
                  [{ id: "", label: "All" }, ...books].find(
                    (book) =>
                      String(book.id) ===
                      String(filters.book || "")
                  ) || null
                }
                onChange={(_, book) =>
                  handleFilterChange(
                    "book",
                    book?.id ? String(book.id) : ""
                  )
                }
                getOptionLabel={(book) =>
                  String(getBookLabel(book) || "")
                }
                isOptionEqualToValue={(option, value) =>
                  String(option.id) === String(value.id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Book"
                    placeholder="Search books..."
                    sx={{
                      "& .MuiInputLabel-root": inputLabelSx,
                    }}
                  />
                )}
                sx={selectSx}
              />

              {/* RESERVATION STATUS */}

              <FormControl
                fullWidth
                size="medium"
                sx={{
                  "& .MuiInputLabel-root": inputLabelSx,
                }}
              >
                <InputLabel>
                  Reservation Status
                </InputLabel>

                <Select
                  label="Reservation Status"
                  value={
                    filters.reservation_status || ""
                  }
                  onChange={(e) =>
                    handleFilterChange(
                      "reservation_status",
                      e.target.value
                    )
                  }
                  sx={selectSx}
                  MenuProps={menuProps}
                >
                  <MenuItem value="">All</MenuItem>

                  {RESERVATION_STATUSES.map(
                    (status) => (
                      <MenuItem
                        key={status.value}
                        value={status.value}
                      >
                        {status.label}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>

              {/* RESERVATION DATE (single, per Swagger) */}

              <TextField
                fullWidth
                type="date"
                label="Reservation Date"
                InputLabelProps={{ shrink: true }}
                value={
                  filters.reservation_date || ""
                }
                onChange={(e) =>
                  handleFilterChange(
                    "reservation_date",
                    e.target.value
                  )
                }
                sx={{ ...textFieldSx, ...datePickerSx }}
              />

              {/* EXPIRY DATE (single, per Swagger) */}

              <TextField
                fullWidth
                type="date"
                label="Expiry Date"
                InputLabelProps={{ shrink: true }}
                value={filters.expiry_date || ""}
                onChange={(e) =>
                  handleFilterChange(
                    "expiry_date",
                    e.target.value
                  )
                }
                sx={{ ...textFieldSx, ...datePickerSx }}
              />

              {/* ACTIVE STATUS */}

              <FormControl
                fullWidth
                size="medium"
                sx={{
                  "& .MuiInputLabel-root": inputLabelSx,
                }}
              >
                <InputLabel>Status</InputLabel>

                <Select
                  label="Status"
                  value={filters.is_active || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "is_active",
                      e.target.value
                    )
                  }
                  sx={selectSx}
                  MenuProps={menuProps}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">
                    Active
                  </MenuItem>
                  <MenuItem value="false">
                    Inactive
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          </CustomScrollbar>

          {/* FOOTER */}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              p: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              flexShrink: 0,
            }}
          >
            <Button
              variant="contained"
              onClick={() =>
                setOpenFilterDialog(false)
              }
            >
              CANCEL
            </Button>

            <Button
              variant="contained"
              color="error"
              onClick={handleResetFilters}
            >
              RESET
            </Button>

            <Button
              variant="contained"
              onClick={handleApplyFilters}
            >
              APPLY
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* =====================================================
          TABLE
      ===================================================== */}

      <Paper
        elevation={3}
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor:
            theme.palette.background.paper,
          color: textColor,
          border: `1px solid ${
            isDark
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)"
          }`,
        }}
      >
        <Divider />

        <TableContainer
          sx={{
            overflowX: "auto",
            ...scrollbarStyles(theme),
          }}
        >
          <Table
            sx={{
              minWidth: 1200,

              "& .MuiTableCell-root": {
                color: `${textColor} !important`,
                borderColor: theme.palette.divider,
              },

              "& .MuiTableBody-root .MuiTableRow-root":
                {
                  backgroundColor:
                    theme.palette.background.paper,
                },

              "& .MuiTableBody-root .MuiTableRow-root:hover":
                {
                  backgroundColor: tableRowHover,
                },

              "& .MuiTableHead-root .MuiTableCell-root":
                {
                  fontWeight: 700,
                  backgroundColor:
                    theme.palette.primary.main,
                  color: `${theme.palette.primary.contrastText} !important`,
                  whiteSpace: "nowrap",
                },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>

                <TableCell>
                  Reservation Code
                </TableCell>

                <TableCell>Member</TableCell>

                <TableCell>Book</TableCell>

                <TableCell>
                  Reservation Date
                </TableCell>

                <TableCell>Expiry Date</TableCell>

                <TableCell>Queue</TableCell>

                <TableCell>Status</TableCell>

                <TableCell>Active</TableCell>

                <TableCell align="center">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    align="center"
                    sx={{ py: 5 }}
                  >
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length > 0 ? (
                rows.map((row, index) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      {page * rowsPerPage + index + 1}
                    </TableCell>

                    <TableCell>
                      <Typography
                        sx={{
                          color: `${textColor} !important`,
                          fontWeight: 600,
                        }}
                      >
                        {row.reservation_code || "-"}
                      </Typography>
                    </TableCell>

                    {/* MEMBER */}

                    <TableCell>
                      <Typography
                        component="div"
                        sx={{
                          color: `${textColor} !important`,
                          fontWeight: 600,
                        }}
                      >
                        {row.member_name || "-"}
                      </Typography>

                      <Typography
                        component="div"
                        variant="caption"
                        sx={{
                          color: `${secondaryTextColor} !important`,
                          display: "block",
                          mt: 0.3,
                        }}
                      >
                        {row.member_code || "-"}
                      </Typography>
                    </TableCell>

                    {/* BOOK */}

                    <TableCell>
                      <Typography
                        component="div"
                        sx={{
                          color: `${textColor} !important`,
                          fontWeight: 600,
                        }}
                      >
                        {row.book_title || "-"}
                      </Typography>

                      <Typography
                        component="div"
                        variant="caption"
                        sx={{
                          color: `${secondaryTextColor} !important`,
                          display: "block",
                          mt: 0.3,
                        }}
                      >
                        {row.book_code || "-"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {row.reservation_date || "-"}
                    </TableCell>

                    <TableCell>
                      {row.expiry_date || "-"}
                    </TableCell>

                    {/* QUEUE POSITION
                        (backend-calculated priority_number) */}

                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          row.priority_number
                            ? `#${row.priority_number}`
                            : "-"
                        }
                        sx={{
                          fontWeight: 700,
                          borderRadius: "20px",
                          backgroundColor:
                            "rgba(25,118,210,0.12)",
                          color:
                            theme.palette.primary.main,
                          border: "none",
                        }}
                      />
                    </TableCell>

                    {/* RESERVATION STATUS */}

                    <TableCell>
                      {statusChip(row)}
                    </TableCell>

                    {/* ACTIVE */}

                    <TableCell>
                      <Chip
                        size="small"
                        icon={
                          row.is_active ? (
                            <CheckCircle />
                          ) : (
                            <Cancel />
                          )
                        }
                        label={
                          row.is_active
                            ? "Active"
                            : "Inactive"
                        }
                        sx={{
                          fontWeight: 600,
                          borderRadius: "20px",

                          backgroundColor: row.is_active
                            ? "rgba(46,125,50,0.12)"
                            : "rgba(211,47,47,0.12)",

                          color: row.is_active
                            ? theme.palette.success.main
                            : theme.palette.error.main,

                          "& .MuiChip-icon": {
                            color: row.is_active
                              ? theme.palette.success.main
                              : theme.palette.error.main,
                          },

                          border: "none",
                        }}
                      />
                    </TableCell>

                    {/* ACTIONS */}

                    <TableCell align="center">
                      <Stack
                        direction="row"
                        justifyContent="center"
                        spacing={0.5}
                      >
                        <Tooltip title="View">
                          <IconButton
                            color="info"
                            onClick={() =>
                              fetchDetail(
                                row.id,
                                "view"
                              )
                            }
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Edit">
                          <IconButton
                            color="primary"
                            onClick={() =>
                              fetchDetail(
                                row.id,
                                "edit"
                              )
                            }
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>

                        <Tooltip
                          title={
                            row.is_active
                              ? "Deactivate"
                              : "Activate"
                          }
                        >
                          <IconButton
                            color={
                              row.is_active
                                ? "warning"
                                : "success"
                            }
                            onClick={() =>
                              openStatusConfirmation(
                                row
                              )
                            }
                          >
                            {row.is_active ? (
                              <Cancel />
                            ) : (
                              <CheckCircle />
                            )}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            onClick={() =>
                              openDeleteConfirmation(
                                row
                              )
                            }
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    align="center"
                    sx={{
                      py: 5,
                      color: `${secondaryTextColor} !important`,
                    }}
                  >
                    No book reservations found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={allRows.length}
          page={page}
          onPageChange={(_event, newPage) =>
            setPage(newPage)
          }
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(
              parseInt(event.target.value, 10)
            );

            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            color: textColor,

            "& .MuiTablePagination-selectLabel": {
              color: textColor,
            },

            "& .MuiTablePagination-displayedRows": {
              color: textColor,
            },

            "& .MuiSelect-select": {
              color: textColor,
            },

            "& .MuiSvgIcon-root": {
              color: textColor,
            },
          }}
        />
      </Paper>

      {/* =====================================================
          CREATE / VIEW / EDIT MODAL
      ===================================================== */}

      <Modal
        open={openDialog}
        onClose={() =>
          !formLoading && setOpenDialog(false)
        }
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: 600, md: 850 },
            maxWidth: "95%",
            maxHeight: "90vh",
            bgcolor: theme.palette.background.paper,
            color: modalTextColor,
            borderRadius: 3,
            boxShadow: 24,
            overflow: "hidden",
            outline: "none",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* HEADER */}

          <Box
            sx={{
              backgroundColor:
                theme.palette.primary.main,
              px: 2,
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: "#ffffff",
                  fontWeight: 700,
                }}
              >
                {dialogMode === "create"
                  ? "Add Reservation"
                  : dialogMode === "edit"
                  ? "Edit Book Reservation"
                  : "Book Reservation Details"}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255,255,255,0.85)",
                  mt: 0.3,
                }}
              >
                {dialogMode === "create"
                  ? "Reserve a book with no available copies."
                  : "View and manage book reservation details."}
              </Typography>
            </Box>

            <IconButton
              onClick={() =>
                !formLoading && setOpenDialog(false)
              }
              sx={{ color: "#ffffff" }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* BODY */}

          <CustomScrollbar
            sx={{
              p: 3,
              maxHeight: "calc(90vh - 135px)",
            }}
          >
            {formLoading ? (
              <Box
                minHeight={300}
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                    },
                    gap: 2,
                  }}
                >
                  {/* LIBRARY MEMBER */}

                  <Autocomplete
                    fullWidth
                    options={members}
                    value={
                      members.find(
                        (member) =>
                          String(member.id) ===
                          String(form.library_member || "")
                      ) || null
                    }
                    onChange={(_, member) =>
                      handleFormChange(
                        "library_member",
                        member ? String(member.id) : ""
                      )
                    }
                    getOptionLabel={(member) =>
                      String(getMemberLabel(member) || "")
                    }
                    isOptionEqualToValue={(option, value) =>
                      String(option.id) === String(value.id)
                    }
                    disabled={
                      dialogMode === "view" || formLoading
                    }
                    noOptionsText="No library members found"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Library Member *"
                        placeholder="Search members..."
                        sx={{
                          ...textFieldSx,
                          "& .MuiInputLabel-root": inputLabelSx,
                        }}
                      />
                    )}
                    sx={selectSx}
                  />

                  {/* BOOK */}

                  <Autocomplete
                    fullWidth
                    options={books}
                    value={
                      books.find(
                        (book) =>
                          String(book.id) ===
                          String(form.book || "")
                      ) || null
                    }
                    onChange={(_, book) =>
                      handleFormChange(
                        "book",
                        book ? String(book.id) : ""
                      )
                    }
                    getOptionLabel={(book) =>
                      String(getBookLabel(book) || "")
                    }
                    isOptionEqualToValue={(option, value) =>
                      String(option.id) === String(value.id)
                    }
                    getOptionDisabled={(book) =>
                      !isBookReservable(book) &&
                      dialogMode !== "view"
                    }
                    disabled={
                      dialogMode === "view" || formLoading
                    }
                    noOptionsText="No books found"
                    renderOption={(props, book) => {
                      const availability =
                        getBookAvailability(book);
                      const canReserve =
                        isBookReservable(book);

                      return (
                        <li {...props} key={book.id}>
                          {getBookLabel(book)}
                          {availability !== null && (
                            <Typography
                              component="span"
                              sx={{
                                ml: 1,
                                fontSize: 12,
                                color: canReserve
                                  ? theme.palette.success.main
                                  : theme.palette.error.main,
                                fontWeight: 600,
                              }}
                            >
                              {availability === 0
                                ? "(Available: 0 — Can Reserve)"
                                : `(Available: ${availability} — Issue Instead)`}
                            </Typography>
                          )}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Book *"
                        placeholder="Search books..."
                        sx={{
                          ...textFieldSx,
                          "& .MuiInputLabel-root": inputLabelSx,
                        }}
                      />
                    )}
                    sx={selectSx}
                  />

                  {/* RESERVATION DATE */}

                  <TextField
                    fullWidth
                    type="date"
                    label="Reservation Date *"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={
                      form.reservation_date || ""
                    }
                    disabled={
                      dialogMode === "view" ||
                      formLoading
                    }
                    onChange={(e) =>
                      handleFormChange(
                        "reservation_date",
                        e.target.value
                      )
                    }
                    sx={{ ...textFieldSx, ...datePickerSx }}
                  />

                  {/* EXPIRY DATE */}

                  <TextField
                    fullWidth
                    type="date"
                    label="Expiry Date"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={form.expiry_date || ""}
                    disabled={
                      dialogMode === "view" ||
                      formLoading
                    }
                    onChange={(e) =>
                      handleFormChange(
                        "expiry_date",
                        e.target.value
                      )
                    }
                    sx={{ ...textFieldSx, ...datePickerSx }}
                  />

                  {/* RESERVATION STATUS (edit/view only) */}

                  {dialogMode !== "create" && (
                    <FormControl
                      fullWidth
                      sx={{
                        "& .MuiInputLabel-root":
                          inputLabelSx,
                      }}
                    >
                      <InputLabel>
                        Reservation Status
                      </InputLabel>

                      <Select
                        label="Reservation Status"
                        value={
                          form.reservation_status ||
                          "pending"
                        }
                        disabled={
                          dialogMode === "view" ||
                          formLoading
                        }
                        onChange={(e) =>
                          handleFormChange(
                            "reservation_status",
                            e.target.value
                          )
                        }
                        sx={selectSx}
                        MenuProps={menuProps}
                      >
                        {RESERVATION_STATUSES.map(
                          (status) => (
                            <MenuItem
                              key={status.value}
                              value={status.value}
                            >
                              {status.label}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                  )}

                  {/* NOTIFICATION SENT (edit only, per Swagger update payload) */}

                  {dialogMode === "edit" && (
                    <FormControl
                      fullWidth
                      sx={{
                        "& .MuiInputLabel-root":
                          inputLabelSx,
                      }}
                    >
                      <InputLabel>
                        Notification Sent
                      </InputLabel>

                      <Select
                        label="Notification Sent"
                        value={
                          form.notification_sent
                            ? "true"
                            : "false"
                        }
                        disabled={formLoading}
                        onChange={(e) =>
                          handleFormChange(
                            "notification_sent",
                            e.target.value === "true"
                          )
                        }
                        sx={selectSx}
                        MenuProps={menuProps}
                      >
                        <MenuItem value="true">
                          Yes
                        </MenuItem>

                        <MenuItem value="false">
                          No
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Box>

                {/* ACTIVE STATUS (edit only) */}

                {dialogMode === "edit" && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mt: 2,
                      p: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${borderColor}`,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          color: `${textColor} !important`,
                          fontWeight: 600,
                        }}
                      >
                        Active Status
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          color: `${secondaryTextColor} !important`,
                          display: "block",
                        }}
                      >
                        Deactivated reservations are
                        hidden from normal lists.
                      </Typography>
                    </Box>

                    <Switch
                      checked={Boolean(
                        form.is_active
                      )}
                      disabled={formLoading}
                      onChange={(e) =>
                        handleFormChange(
                          "is_active",
                          e.target.checked
                        )
                      }
                      color="success"
                    />
                  </Box>
                )}

                {/* REMARKS */}

                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Remarks"
                  value={form.remarks || ""}
                  disabled={
                    dialogMode === "view" ||
                    formLoading
                  }
                  onChange={(e) =>
                    handleFormChange(
                      "remarks",
                      e.target.value
                    )
                  }
                  sx={{ ...textFieldSx, mt: 2 }}
                />
              </>
            )}
          </CustomScrollbar>

          {/* FOOTER */}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              p: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              flexShrink: 0,
            }}
          >
            <Button
              variant="contained"
              onClick={() =>
                !formLoading && setOpenDialog(false)
              }
              disabled={formLoading}
              sx={{
                color: textColor,
                borderColor,

                "&:hover": {
                  borderColor:
                    theme.palette.primary.main,
                },
              }}
            >
              CLOSE
            </Button>

            {dialogMode === "create" && (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={formLoading}
              >
                {formLoading ? (
                  <CircularProgress
                    size={22}
                    color="inherit"
                  />
                ) : (
                  "RESERVE BOOK"
                )}
              </Button>
            )}

            {dialogMode === "edit" && (
              <Button
                variant="contained"
                onClick={handleUpdate}
                disabled={formLoading}
              >
                {formLoading ? (
                  <CircularProgress
                    size={22}
                    color="inherit"
                  />
                ) : (
                  "UPDATE"
                )}
              </Button>
            )}
          </Box>
        </Box>
      </Modal>

      {/* =====================================================
          DELETE / ACTIVATE / DEACTIVATE CONFIRMATION
      ===================================================== */}

      <Modal
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92%", sm: 430 },
            bgcolor: theme.palette.background.paper,
            color: modalTextColor,
            borderRadius: 3,
            boxShadow: 24,
            outline: "none",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor:
                confirmDialog.type === "delete"
                  ? theme.palette.error.main
                  : confirmDialog.row?.is_active
                  ? theme.palette.warning.main
                  : theme.palette.success.main,
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#ffffff",
                fontWeight: 700,
              }}
            >
              {confirmDialog.type === "delete"
                ? "Delete Reservation"
                : confirmDialog.row?.is_active
                ? "Deactivate Reservation"
                : "Activate Reservation"}
            </Typography>

            <IconButton
              onClick={closeConfirmDialog}
              sx={{ color: "#ffffff" }}
            >
              <Close />
            </IconButton>
          </Box>

          <Box sx={{ p: 3 }}>
            <Box
              display="flex"
              justifyContent="center"
              mb={2}
            >
              {confirmDialog.type === "delete" ? (
                <Delete
                  sx={{
                    fontSize: 48,
                    color: theme.palette.error.main,
                  }}
                />
              ) : confirmDialog.row?.is_active ? (
                <Cancel
                  sx={{
                    fontSize: 48,
                    color:
                      theme.palette.warning.main,
                  }}
                />
              ) : (
                <CheckCircle
                  sx={{
                    fontSize: 48,
                    color:
                      theme.palette.success.main,
                  }}
                />
              )}
            </Box>

            <Typography
              align="center"
              sx={{
                color: modalTextColor,
                fontWeight: 600,
                mb: 1,
              }}
            >
              {confirmDialog.type === "delete"
                ? "Are you sure you want to delete this reservation?"
                : confirmDialog.row?.is_active
                ? "Are you sure you want to deactivate this reservation?"
                : "Are you sure you want to activate this reservation?"}
            </Typography>

            {confirmDialog.row && (
              <Typography
                align="center"
                variant="body2"
                sx={{
                  color: secondaryTextColor,
                  mb: 3,
                }}
              >
                {confirmDialog.row.reservation_code ||
                  "Reservation"}
              </Typography>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
              }}
            >
              <Button
                variant="contained"
                onClick={closeConfirmDialog}
                disabled={confirmLoading}
                sx={{
                  color: textColor,
                  borderColor,
                }}
              >
                CANCEL
              </Button>

              <Button
                variant="contained"
                color={
                  confirmDialog.type === "delete"
                    ? "error"
                    : confirmDialog.row?.is_active
                    ? "warning"
                    : "success"
                }
                onClick={
                  confirmDialog.type === "delete"
                    ? handleDelete
                    : handleStatus
                }
                disabled={confirmLoading}
              >
                {confirmLoading ? (
                  <CircularProgress
                    size={22}
                    color="inherit"
                  />
                ) : confirmDialog.type ===
                  "delete" ? (
                  "DELETE"
                ) : confirmDialog.row?.is_active ? (
                  "DEACTIVATE"
                ) : (
                  "ACTIVATE"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}